import React, { useCallback, useEffect, useState } from "react";
import {
  apiErrorMessage,
  createInvoiceDescriptionPreset,
  deleteInvoiceDescriptionPreset,
  getInvoiceDescriptionPresets,
  reorderInvoiceDescriptionPresets,
  updateInvoiceDescriptionPreset,
} from "../../api";

/**
 * The standard invoice line wordings the Create Invoice picker offers, one
 * list per kind of client. Managers edit them here; staff only read them.
 *
 * Retiring a wording is a Deactivate, not a delete: it leaves the picker but
 * stays here, greyed, to be brought back. Only an already-deactivated row can
 * be deleted outright.
 */

const CATEGORIES = [
  { key: "corporate", name: "Corporate" },
  { key: "personal", name: "Personal" },
];

const byOrder = (a, b) => a.sort_order - b.sort_order || a.id - b.id;

const input = {
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 13,
  width: "100%",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const smallButton = {
  fontSize: 12,
  padding: "5px 10px",
  background: "white",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  cursor: "pointer",
  fontWeight: 500,
};

const primaryButton = {
  ...smallButton,
  background: "#111827",
  color: "white",
  border: "1px solid #111827",
  fontWeight: 600,
};

export default function InvoiceDescriptionsSection() {
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [adding, setAdding] = useState({ corporate: { label: "", text: "" }, personal: { label: "", text: "" } });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getInvoiceDescriptionPresets();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
      setError(apiErrorMessage(e, "Could not load the invoice descriptions."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Runs a change, shows what the server said if it refused. */
  const run = async (fn, fallback) => {
    setError("");
    setBusy(true);
    try {
      await fn();
      return true;
    } catch (e) {
      setError(apiErrorMessage(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const replaceRow = (saved) =>
    setRows((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));

  const draftOf = (row) => drafts[row.id] || { label: row.label, text: row.text };
  const isDirty = (row) => {
    const d = drafts[row.id];
    return !!d && (d.label !== row.label || d.text !== row.text);
  };
  const setDraft = (row, field, value) =>
    setDrafts((prev) => ({ ...prev, [row.id]: { ...draftOf(row), [field]: value } }));
  const dropDraft = (id) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const save = (row) =>
    run(async () => {
      const d = draftOf(row);
      const saved = await updateInvoiceDescriptionPreset(row.id, { label: d.label, text: d.text });
      replaceRow(saved);
      dropDraft(row.id);
    }, "Could not save the description.");

  const setActive = (row, isActive) =>
    run(async () => {
      replaceRow(await updateInvoiceDescriptionPreset(row.id, { is_active: isActive }));
    }, isActive ? "Could not reactivate the description." : "Could not deactivate the description.");

  const remove = (row) => {
    if (!window.confirm(`Delete "${row.label}" for good? This cannot be undone.`)) return;
    run(async () => {
      await deleteInvoiceDescriptionPreset(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      dropDraft(row.id);
    }, "Could not delete the description.");
  };

  /** Swap a row with its neighbour among the active rows of its list. */
  const move = (category, row, delta) => {
    const active = rows.filter((r) => r.category === category && r.is_active).sort(byOrder);
    const inactive = rows.filter((r) => r.category === category && !r.is_active).sort(byOrder);
    const from = active.findIndex((r) => r.id === row.id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= active.length) return;
    const reordered = [...active];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    const ids = [...reordered, ...inactive].map((r) => r.id);
    run(async () => {
      const updated = await reorderInvoiceDescriptionPresets(category, ids);
      const byId = new Map((Array.isArray(updated) ? updated : []).map((r) => [r.id, r]));
      setRows((prev) => prev.map((r) => byId.get(r.id) || r));
    }, "Could not move the description.");
  };

  const add = (category) =>
    run(async () => {
      const { label, text } = adding[category];
      const created = await createInvoiceDescriptionPreset({ category, label, text });
      setRows((prev) => [...prev, created]);
      setAdding((prev) => ({ ...prev, [category]: { label: "", text: "" } }));
    }, "Could not add the description.");

  if (rows === null) return <div style={{ fontSize: 13, color: "#6b7280" }}>Loading…</div>;

  return (
    <div>
      <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0, marginBottom: 16 }}>
        The standard wordings offered on each invoice line. <code>{"{YEAR}"}</code> and{" "}
        <code>{"{YEAR_END}"}</code> are filled in on the invoice.
      </p>

      {error && (
        <div
          role="alert"
          style={{
            background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
            borderRadius: 8, padding: "10px 12px", marginBottom: 16, fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {CATEGORIES.map(({ key, name }) => {
        const active = rows.filter((r) => r.category === key && r.is_active).sort(byOrder);
        const inactive = rows.filter((r) => r.category === key && !r.is_active).sort(byOrder);
        const draft = adding[key];
        return (
          <div
            key={key}
            role="group"
            aria-label={`${name} descriptions`}
            style={{ marginBottom: 28 }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: "0 0 10px" }}>
              {name}
            </h3>

            {active.length === 0 && (
              <p style={{ fontSize: 13, color: "#6b7280" }}>
                No active {name.toLowerCase()} descriptions.
              </p>
            )}

            {[...active, ...inactive].map((row) => {
              const d = draftOf(row);
              const pos = active.indexOf(row);
              return (
                <div
                  key={row.id}
                  data-testid={`preset-row-${row.id}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(140px, 220px) minmax(200px, 1fr) auto",
                    gap: 10,
                    alignItems: "start",
                    padding: "10px 0",
                    borderTop: "1px solid #f3f4f6",
                    opacity: row.is_active ? 1 : 0.55,
                  }}
                >
                  <input
                    type="text"
                    aria-label={`Label for ${row.label}`}
                    value={d.label}
                    maxLength={100}
                    onChange={(e) => setDraft(row, "label", e.target.value)}
                    style={input}
                  />
                  <textarea
                    aria-label={`Text for ${row.label}`}
                    value={d.text}
                    rows={2}
                    onChange={(e) => setDraft(row, "text", e.target.value)}
                    style={{ ...input, resize: "vertical" }}
                  />
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      onClick={() => save(row)}
                      disabled={busy || !isDirty(row)}
                      aria-label={`Save ${row.label}`}
                      style={primaryButton}
                    >
                      Save
                    </button>
                    {row.is_active ? (
                      <>
                        <button
                          type="button"
                          onClick={() => move(key, row, -1)}
                          disabled={busy || pos <= 0}
                          aria-label={`Move ${row.label} up`}
                          style={smallButton}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => move(key, row, 1)}
                          disabled={busy || pos >= active.length - 1}
                          aria-label={`Move ${row.label} down`}
                          style={smallButton}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => setActive(row, false)}
                          disabled={busy}
                          aria-label={`Deactivate ${row.label}`}
                          style={smallButton}
                        >
                          Deactivate
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setActive(row, true)}
                          disabled={busy}
                          aria-label={`Reactivate ${row.label}`}
                          style={smallButton}
                        >
                          Reactivate
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(row)}
                          disabled={busy}
                          aria-label={`Delete ${row.label}`}
                          style={{ ...smallButton, color: "#b91c1c" }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(140px, 220px) minmax(200px, 1fr) auto",
                gap: 10,
                alignItems: "start",
                paddingTop: 12,
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <input
                type="text"
                aria-label={`New ${name.toLowerCase()} label`}
                placeholder="Label"
                value={draft.label}
                maxLength={100}
                onChange={(e) =>
                  setAdding((prev) => ({ ...prev, [key]: { ...prev[key], label: e.target.value } }))}
                style={input}
              />
              <textarea
                aria-label={`New ${name.toLowerCase()} text`}
                placeholder="Wording, e.g. Preparation of {YEAR} …"
                value={draft.text}
                rows={2}
                onChange={(e) =>
                  setAdding((prev) => ({ ...prev, [key]: { ...prev[key], text: e.target.value } }))}
                style={{ ...input, resize: "vertical" }}
              />
              <button
                type="button"
                onClick={() => add(key)}
                disabled={busy}
                aria-label={`Add ${name.toLowerCase()} description`}
                style={primaryButton}
              >
                Add description
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
